import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import {
  GetApp,
  Close,
  Computer,
  PhoneAndroid,
  Tablet,
} from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Export a way to trigger the prompt from other components
export let showInstallPrompt: () => void = () => {};

const InstallPrompt: React.FC = () => {
  const theme = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showInstallSnackbar, setShowInstallSnackbar] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Set up the trigger function
  useEffect(() => {
    showInstallPrompt = () => {
      if (!isInstalled && deferredPrompt && !localStorage.getItem('pwa-install-dismissed')) {
        setShowInstallSnackbar(true);
      }
    };
  }, [isInstalled, deferredPrompt]);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if running as installed PWA
    if ((window.navigator as any).standalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Don't show immediately - let Dashboard control when to show
      // Just store the prompt for later use
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowInstallDialog(false);
      setShowInstallSnackbar(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For browsers that don't support beforeinstallprompt or if it's not available
      setShowInstallDialog(true);
      return;
    }

    // Show the browser's install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // We've used the prompt, and can't use it again
    setDeferredPrompt(null);
    setShowInstallSnackbar(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    setShowInstallSnackbar(false);
  };

  // Don't show anything if already installed
  if (isInstalled) {
    return null;
  }

  return (
    <>
      {/* Install Snackbar */}
      <Snackbar
        open={showInstallSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleInstallClick}
                sx={{ alignSelf: 'center' }}
              >
                Install
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleDismiss}
                sx={{ alignSelf: 'center' }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{ 
            width: '100%',
            alignItems: 'center',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiAlert-action': {
              alignItems: 'center',
              padding: 0,
              paddingLeft: '16px',
            }
          }}
        >
          Install IT Control Panel for easier access!
        </Alert>
      </Snackbar>

      {/* Manual Install Instructions Dialog */}
      <Dialog
        open={showInstallDialog}
        onClose={() => setShowInstallDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GetApp />
            Install IT Control Panel
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Install this app on your device for a better experience:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Computer /> Desktop (Chrome/Edge)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click the install icon in the address bar (right side) or use the menu (⋮) → "Install IT Control Panel"
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneAndroid /> Mobile (Android)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tap the menu (⋮) → "Add to Home screen" or "Install app"
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tablet /> iOS (Safari)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tap the share button → "Add to Home Screen"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallPrompt;